import type { CheckedState } from '@radix-ui/react-checkbox';
import * as RadixSelect from '@radix-ui/react-select';
import { useCallback, useMemo, useState } from 'react';
import { Tooltip } from '~/components/granular/tooltip/Tooltip';
import { classNames } from '~/utils/styles';
import { useDropdownContext } from '../../Dropdown';
import type { DropdownObjectItem } from '../../Dropdown.types';

export function DropdownItem({
  children,
  isSelected,
  item,
  className,
  style,
}: {
  item: string | DropdownObjectItem;
  className?: string;
  isSelected?: CheckedState;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const {
    disabledIncompatibleItems,
    disableNonSelectedItems,
    disabledItemTooltipText,
    isMultiple,
    handleValueChange,
    getLabel,
    getValue,
  } = useDropdownContext();

  const itemObject = useMemo(() => (typeof item === 'string' ? { label: item, value: item } : item), [item]);
  const isItemDisabled = useMemo(
    () =>
      itemObject?.disabled ||
      (disableNonSelectedItems && !isSelected) ||
      (disabledIncompatibleItems.includes(getValue(itemObject)) && !isSelected),
    [itemObject, disableNonSelectedItems, disabledIncompatibleItems, getValue, isSelected],
  );

  // A disabled item has to explain itself however short its label is, so the reason overrides the
  // overflow-only tooltip below rather than waiting on the label being clipped. An item's own reason wins
  // over the dropdown-wide one.
  const disabledReasonText = itemObject.disabledTooltipText ?? disabledItemTooltipText;
  const hasDisabledReason = Boolean(disabledReasonText?.length) && Boolean(isItemDisabled);
  const hasOwnTooltip = Boolean(itemObject.tooltipText?.length);

  const classes = classNames(
    'body-200-light w-full text-neutral-1000',
    'hover:cursor-pointer hover:bg-neutral-200 focus-visible:outline-hidden',
    {
      'cursor-not-allowed! text-neutral-400 hover:bg-transparent': isItemDisabled,
      'bg-teal-300 hover:bg-teal-300': isSelected,
    },
    className,
  );

  function handleItemClick(item: string | DropdownObjectItem) {
    if (isItemDisabled) {
      return;
    }

    const value = getValue(item);

    // handle shortcut items
    if (typeof item !== 'string' && item.isShortcut) {
      handleValueChange([value]); // toggle shortcut item
      handleValueChange(value.split('/').map((v) => v.trim())); // toggle items
      // handle everything else
    } else {
      handleValueChange(isMultiple ? [value] : value);
    }
  }

  // --- Overflow detection logic ---
  const [isOverflowed, setIsOverflowed] = useState(false);

  const textRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) {
      return;
    }
    // if element has a nested label it means it's a checkbox
    const labelElement = el.querySelector('label');
    const labelOverflowed = labelElement
      ? labelElement.scrollWidth > labelElement.clientWidth
      : el.scrollWidth > el.clientWidth;

    setIsOverflowed(labelOverflowed);
  }, []);

  return (
    <RadixSelect.Item
      className={classes}
      disabled={isItemDisabled}
      onClick={() => handleItemClick(itemObject)}
      style={style}
      value={getValue(itemObject)}
    >
      <Tooltip
        disabled={!isOverflowed && !hasDisabledReason && !hasOwnTooltip}
        side="right"
        sideOffset={20}
        trigger={
          <div
            className={classNames(
              '[&>div>div]:block [&>div>div]:overflow-hidden [&>div>div]:text-ellipsis',
              '[&_label]:block! [&_label]:overflow-hidden [&_label]:text-ellipsis',
              { '[&_label]:cursor-not-allowed [&_label]:text-neutral-400': isItemDisabled },
              'px-4 py-2',
              'block overflow-hidden text-ellipsis whitespace-nowrap',
            )}
            ref={textRef}
          >
            {children}
          </div>
        }
      >
        {hasDisabledReason ? disabledReasonText : (itemObject.tooltipText ?? getLabel(itemObject))}
      </Tooltip>
    </RadixSelect.Item>
  );
}
